import React, { useState } from 'react';
import styled from 'styled-components';
import { PRICING_PLANS } from '../config/constants';
import DownloadModal from '../components/DownloadModal';

const PricingSection = styled.section`
  background: transparent;
  min-height: calc(100vh - 80px);
  position: relative;
  padding: 4rem 0;
`;

const HostGuestExplanation = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 3rem;
  margin: 3rem auto;
  max-width: 900px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 2rem auto;
  }
`;

const ExplanationTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const ExplanationText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.15rem;
  line-height: 1.7;
  margin-bottom: 1.25rem;
  font-weight: 400;
`;

const HighlightText = styled.span`
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 5rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: 4rem;
    
    &::before {
      width: 200px;
      top: -2rem;
    }
  }
`;

const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 3rem 2.5rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const PlanTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1.75rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const Price = styled.div`
  font-size: 3.5rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PricePeriod = styled.div`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2.5rem;
  font-size: 1.15rem;
  font-weight: 500;
`;

const PricingFeatures = styled.ul`
  text-align: left;
  margin: 2.5rem 0;
  font-size: 1.05rem;
  list-style: none;
`;

const PricingFeature = styled.li`
  padding: 0.875rem 0;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  font-weight: 400;
  line-height: 1.5;

  &::before {
    content: "✓";
    color: rgba(255, 255, 255, 0.95);
    font-weight: 700;
    margin-right: 1rem;
    background: rgba(255, 255, 255, 0.15);
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
  }
`;

const PricingFeatureDisabled = styled.li`
  padding: 0.875rem 0;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  opacity: 0.6;
  font-weight: 400;
  line-height: 1.5;

  &::before {
    content: "✗";
    color: rgba(255, 255, 255, 0.5);
    font-weight: 700;
    margin-right: 1rem;
    background: rgba(255, 255, 255, 0.1);
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
  }
`;

const TrialNote = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.8);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
  margin-top: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;


const FreeTrialBadge = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.95);
  padding: 0.75rem 1.5rem;
  border-radius: 16px;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 auto 2rem;
  display: block;
  width: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;



const Pricing = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);


  return (
    <>
      <PricingSection>
        <div className="container">
          <h2 style={{ 
            fontSize: '3.5rem', 
            fontWeight: '700', 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            color: 'rgba(255, 255, 255, 0.95)'
          }}>Simple, Transparent Pricing</h2>
          <p style={{
            fontSize: '1.25rem',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '700px',
            margin: '0 auto 3rem',
            lineHeight: '1.6'
          }}>Choose the plan that works best for you and your family</p>
          
          <HostGuestExplanation>
            <ExplanationTitle>How It Works</ExplanationTitle>
            <ExplanationText>
              <HighlightText>Only the group host needs a subscription</HighlightText> to create and manage groups. 
              Everyone else can join groups for free and get full access to all features when invited.
            </ExplanationText>
            <ExplanationText>
              This means one person pays, but everyone in the group benefits from all the features!
            </ExplanationText>
            <FreeTrialBadge>
              All paid plans include a 7-day free trial
            </FreeTrialBadge>
            <button className="cta-button" onClick={() => setIsDownloadModalOpen(true)}>
              Get Started
            </button>
          </HostGuestExplanation>

          <PricingGrid>
            {PRICING_PLANS.map((plan) => (
              <PricingCard key={plan.name}>
                <PlanTitle>{plan.name}</PlanTitle>
                <Price>{plan.price}</Price>
                <PricePeriod>{plan.period}</PricePeriod>
                
                <PricingFeatures>
                  {plan.features.map((feature, index) => (
                    <PricingFeature key={index}>{feature}</PricingFeature>
                  ))}
                  {plan.name === 'Free' && (
                    <PricingFeatureDisabled>Host groups</PricingFeatureDisabled>
                  )}
                </PricingFeatures>
                
                {plan.name !== 'Free' && (
                  <TrialNote>
                    7-day free trial included
                  </TrialNote>
                )}
              </PricingCard>
            ))}
          </PricingGrid>
        </div>
      </PricingSection>
      
      <DownloadModal 
        isOpen={isDownloadModalOpen} 
        onClose={() => setIsDownloadModalOpen(false)} 
      />
    </>
  );
};

export default Pricing;